const RETRY_PARAM = 'admin_chunk_retry'

// Cross-browser wording for a lazy `import()` whose chunk 404s because a newer
// deploy already overwrote it with a different content hash (Cloudflare Pages
// keeps only the latest build's assets, so a tab left open across a deploy can
// still be holding an index.html that points at chunk files no longer served).
const CHUNK_LOAD_ERROR = /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

function isChunkLoadError(message: unknown): boolean {
  return typeof message === 'string' && CHUNK_LOAD_ERROR.test(message)
}

function hasRetryMarker(): boolean {
  return new URLSearchParams(window.location.search).has(RETRY_PARAM)
}

// Whether this load is itself the result of our own reload, read once at install time
// and then stripped from the URL immediately below - sessionStorage can't be used for
// this (a browser with storage disabled would fail every read/write silently, so the
// guard would never persist and a persistent chunk error would reload forever; PR #432
// review). The URL round-trips through our own `location.replace` call either way, so
// it needs no storage API to survive the reload.
let alreadyRetried = false

function clearRetryMarkerFromUrl(): void {
  if (!hasRetryMarker()) return
  const url = new URL(window.location.href)
  url.searchParams.delete(RETRY_PARAM)
  window.history.replaceState(window.history.state, '', url.toString())
}

let reloadTriggered = false

// A plain `location.reload()` can still land back on the exact stale `index.html` that
// got us here if the browser served it from its HTTP cache rather than the network -
// reported as "reload doesn't help, only clearing cache does" (PR #432 review). Force a
// cache-bypassing refetch of the document first so the reload actually picks up the
// current build's chunk manifest.
async function reloadOnce(): Promise<void> {
  if (reloadTriggered || alreadyRetried) return
  reloadTriggered = true

  const url = new URL(window.location.href)
  url.searchParams.set(RETRY_PARAM, '1')

  try {
    await fetch(window.location.href, { cache: 'reload' })
  } catch {
    // offline or blocked - the reload below is still worth attempting regardless
  }
  window.location.replace(url.toString())
}

function handleError(event: ErrorEvent): void {
  if (isChunkLoadError(event.message)) void reloadOnce()
}

function handleRejection(event: PromiseRejectionEvent): void {
  if (isChunkLoadError((event.reason as Error | undefined)?.message)) void reloadOnce()
}

// Safety net for stale lazy-route chunks: unlike boot failure recovery, this stays
// installed for the app's whole lifetime, since a user can navigate to a route that
// was deployed over at any point in a long-lived tab, not just at startup.
export function installChunkLoadRecovery(): void {
  alreadyRetried = hasRetryMarker()
  clearRetryMarkerFromUrl()
  window.addEventListener('error', handleError)
  window.addEventListener('unhandledrejection', handleRejection)
}
